import { bunja, createScope } from 'bunja';
import { atom } from 'jotai';

import { ProductType } from '@flipster/apis/v2/history/types';
import { isTradFiInstrument } from '@flipster/features/src/shared/rwa';
import { rawInstrumentsKeyBySymbolAtom, snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { InstrumentV2ForApiInstanceType } from '@flipster/features/src/shared/snapshot/utils';
import { marketsBunja } from '@flipster/r230522/original/market';
import { orderbooksBunja } from '@flipster/r230522/original/orderbooks';
import { pswapDetailsBunja } from '@flipster/r230522/original/pswap-detail';
import { pswapTickersBunja } from '@flipster/r230522/original/pswap-ticker';
import { spotDetailsBunja } from '@flipster/r230522/original/spot-detail';
import { spotMarketsBunja } from '@flipster/r230522/original/spot-market';
import { spotTickerBunja } from '@flipster/r230522/original/spot-ticker';
import { getSpotTickerDisplayPrice, getTickerDisplayPrice } from '@flipster/r230522/original/ticker-price';
import { atomWithRafThrottle } from '@flipster/react/misc/state/atom-with-raf-throttle';
import { ParamsScope } from '@flipster/react/providers/route';
import { getChartLinkedTickerPrice, normalizeChartPriceType } from '@flipster/tvchart/src/misc/chart-price-type';
import { getTradingViewDataStorageAtom } from '@flipster/tvchart/src/misc/data-manager';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

export const SymbolScope = createScope<string>();

type SymbolInfoType =
  | InstrumentV2ForApiInstanceType
  | (Partial<InstrumentV2ForApiInstanceType> & {
      pricePrecision: number;
      tradeQtyPrecision: number;
      maxLeverageHardLimit: number;
    });

export const tradeSymbolBunja = bunja(() => {
  const params = bunja.use(ParamsScope);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const PlaceholderSymbolInfo = { pricePrecision: 2, tradeQtyPrecision: 2, maxLeverageHardLimit: 100 };

  const symbol = (Array.isArray(params.symbol) ? params.symbol[0] : params.symbol) || 'dummy';

  const rawSymbolInfoAtom = atom((get) => get(rawInstrumentsKeyBySymbolAtom)?.[symbol]);
  const symbolInfoExistsAtom = atom((get) => Boolean(get(rawSymbolInfoAtom)));
  const isSpotAtom = atom((get) => get(rawSymbolInfoAtom)?.isSpot);
  const isFuturesAtom = atom((get) => get(rawSymbolInfoAtom)?.isFutures);
  const isTradFiInstrumentAtom = atom((get) => isTradFiInstrument(get(rawSymbolInfoAtom)));
  const displayNameAtom = atom((get) => get(rawSymbolInfoAtom)?.displayName);
  const fundingTypeAtom = atom((get) => get(rawSymbolInfoAtom)?.fundingType);
  const positionUnitAtom = atom((get) => get(rawSymbolInfoAtom)?.positionUnit);
  const pricePrecisionAtom = atom(
    (get) => get(rawSymbolInfoAtom)?.pricePrecision ?? PlaceholderSymbolInfo.pricePrecision,
  );
  const quoteCurrencyAtom = atom((get) => get(rawSymbolInfoAtom)?.quoteCurrency);
  const baseCurrencyAtom = atom((get) => get(rawSymbolInfoAtom)?.baseCurrency);

  const symbolInfoAtom = atom((get) => {
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    return instrumentsKeyBySymbol?.[symbol];
  });

  const symbolInfoWithDefaultAtom = atom<SymbolInfoType>((get) => {
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    return instrumentsKeyBySymbol?.[symbol] ?? PlaceholderSymbolInfo;
  });

  return {
    symbol,
    isSpotAtom,
    isFuturesAtom,
    isTradFiInstrumentAtom,
    displayNameAtom,
    fundingTypeAtom,
    positionUnitAtom,
    pricePrecisionAtom,
    quoteCurrencyAtom,
    baseCurrencyAtom,
    symbolInfoAtom,
    symbolInfoExistsAtom,
    symbolInfoWithDefaultAtom,
  };
});
tradeSymbolBunja.debugLabel = 'tradeSymbolBunja';

export const perpetualSymbolSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, symbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const { pswapTickersAtom, setCurrentTicker } = bunja.use(pswapTickersBunja);
  const { pswapDetailsAtom, setCurrentSymbol } = bunja.use(pswapDetailsBunja);
  const { setCurrentOrderBook } = bunja.use(orderbooksBunja);

  const { marketsAtom } = bunja.use(marketsBunja);
  const getTickerAtom = atom((get) => {
    const tickers = get(pswapTickersAtom);
    if (!tickers) return undefined;
    return tickers[symbol];
  });

  const getMarketAtom = atom((get) => {
    const markets = get(marketsAtom);
    if (!markets) return undefined;
    return markets[symbol];
  });

  const getDetailAtom = atom((get) => {
    const pswapDetails = get(pswapDetailsAtom);
    if (!pswapDetails) return undefined;
    return pswapDetails[symbol];
  });

  bunja.effect(() => {
    setSubscriptionState();
    // FIXME: unmount시 null을 set하지 않으면 perpetualSymbolSocketBunja가 unmount된 후에도
    // ticker, detail, orderbook 등이 계속 구독중인 상태로 남을 수 있으나, 그걸 해결하면 반대로 동시성 이슈가 발생할 수 있음.
    // 기존구독 구조에서는 제대로 해결하기 까다로운 문제로, 추후 일반구독 구조를 활용해서 ticker, dtail, orderbook을 scope을 통해서 주입받도록 만들어야 함.
    return store.sub(symbolInfoAtom, setSubscriptionState);
    function setSubscriptionState() {
      const symbolInfo = store.get(symbolInfoAtom);
      if (symbolInfo?.productType !== ProductType.Perpetual) return;
      setCurrentTicker(symbol);
      setCurrentSymbol(symbol);
      setCurrentOrderBook(symbol);
    }
  });

  return {
    getTickerAtom,
    getMarketAtom,
    getDetailAtom,
  };
});
perpetualSymbolSocketBunja.debugLabel = 'perpetualSymbolSocketBunja';

export const spotSymbolSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, symbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const { spotTickersAtom, setCurrentTicker } = bunja.use(spotTickerBunja);
  const { spotDetailsAtom, setCurrentSymbol } = bunja.use(spotDetailsBunja);
  const { setCurrentOrderBook } = bunja.use(orderbooksBunja);
  const { spotMarketsAtom } = bunja.use(spotMarketsBunja);
  const getTickerAtom = atom((get) => {
    const tickers = get(spotTickersAtom);
    if (!tickers) return undefined;
    return tickers[symbol];
  });

  const getMarketAtom = atom((get) => {
    const markets = get(spotMarketsAtom);
    if (!markets) return undefined;
    return markets[symbol];
  });

  const getDetailAtom = atom((get) => {
    const spotDetails = get(spotDetailsAtom);
    if (!spotDetails) return undefined;
    return spotDetails[symbol];
  });

  bunja.effect(() => {
    setSubscriptionState();
    // perpetualSymbolSocketBunja 주석 참고
    return store.sub(symbolInfoAtom, setSubscriptionState);
    function setSubscriptionState() {
      const symbolInfo = store.get(symbolInfoAtom);
      if (symbolInfo?.productType !== ProductType.Spot) return;
      setCurrentTicker(symbol);
      setCurrentSymbol(symbol);
      setCurrentOrderBook(symbol);
    }
  });

  return {
    getTickerAtom,
    getMarketAtom,
    getDetailAtom,
  };
});
spotSymbolSocketBunja.debugLabel = 'spotSymbolSocketBunja';

/** 탭 제목 가격을 백그라운드 탭에서도 갱신하기 위한 강제 flush 주기(ms) */
const titleForceRefreshMs = 250;

/**
 * Symbol-scoped display prices for the trade page.
 * - `displayPriceAtom`: realtime (chart-linked on perp, mid on spot) — use in data/order logic.
 * - `throttledDisplayPriceAtom`: RAF-throttled — use in UI that renders the main ticker price.
 * - `centerDisplayPriceAtom` / `throttledCenterDisplayPriceAtom`: orderbook center (mid; ignores chart Mark).
 * - `displayPricePrecisionAtom`: mid uses tick+1.
 *
 * ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본: perp는 last 기준 표시 + instrument tick precision.
 */
export const symbolDisplayPriceBunja = bunja(() => {
  const { isSpotAtom, pricePrecisionAtom } = bunja.use(tradeSymbolBunja);
  const { getTickerAtom: getPerpetualTickerAtom, getMarketAtom: getPerpetualMarketAtom } =
    bunja.use(perpetualSymbolSocketBunja);
  const { getTickerAtom: getSpotTickerAtom, getMarketAtom: getSpotMarketAtom } = bunja.use(spotSymbolSocketBunja);
  const chartPriceTypeAtom = getTradingViewDataStorageAtom('priceType');

  const tickerAtom = atom((get) => (get(isSpotAtom) ? get(getSpotTickerAtom) : get(getPerpetualTickerAtom)));
  const tickerOrMarketAtom = atom((get) =>
    get(isSpotAtom)
      ? (get(getSpotTickerAtom) ?? get(getSpotMarketAtom))
      : (get(getPerpetualTickerAtom) ?? get(getPerpetualMarketAtom)),
  );

  const tickerExistsAtom = atom((get) => Boolean(get(tickerOrMarketAtom)));

  const centerDisplayPriceAtom = atom((get) => {
    const source = get(tickerOrMarketAtom);
    if (!source) return undefined;
    return get(isSpotAtom) ? getSpotTickerDisplayPrice(source) : getTickerDisplayPrice(source);
  });

  const displayPriceAtom = atom((get) => {
    const source = get(tickerOrMarketAtom);
    if (!source) return undefined;
    if (get(isSpotAtom)) return getSpotTickerDisplayPrice(source);
    return getChartLinkedTickerPrice(source, normalizeChartPriceType(get(chartPriceTypeAtom)));
  });

  // displayPrice는 브라우저 탭 제목(BrowserTitle)에 노출되므로 백그라운드 탭(rAF 정지)에서도 갱신돼야 한다
  // → forceRefreshPeriod로 주기적 강제 flush를 켠다. center(오더북 중앙가)는 화면에 있을 때만 의미가 있어 순수 rAF.
  const throttledDisplayPriceAtom = atomWithRafThrottle(displayPriceAtom, titleForceRefreshMs);
  const throttledCenterDisplayPriceAtom = atomWithRafThrottle(centerDisplayPriceAtom);

  const priceChange24hAtom = atom((get) => get(tickerAtom)?.priceChange24h);
  const priceChangePct24hAtom = atom((get) => get(tickerAtom)?.priceChangePct24h);

  // Mid = (bid+ask)/2 can land on half-tick; show one extra decimal.
  // context: https://prextech.slack.com/archives/C07DN09JTUY/p1758073579533969
  //
  // ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본: spot만 +1, perp LAST/MARK는 instrument tick 유지.
  // perp 표시가가 mid로 임시 회귀하면서 perp도 half-tick 표기를 위해 +1 적용.
  const displayPricePrecisionAtom = atom((get) => {
    const basePrecision = get(pricePrecisionAtom);
    // return get(isSpotAtom) ? basePrecision + 1 : basePrecision;
    return basePrecision + 1;
  });

  return {
    tickerExistsAtom,
    displayPriceAtom,
    throttledDisplayPriceAtom,
    centerDisplayPriceAtom,
    throttledCenterDisplayPriceAtom,
    pricePrecisionAtom,
    displayPricePrecisionAtom,
    priceChange24hAtom,
    priceChangePct24hAtom,
  };
});
symbolDisplayPriceBunja.debugLabel = 'symbolDisplayPriceBunja';
